import { useMemo } from 'react';
import { useQuery, QueryOptions } from 'react-query';
import UnitService from '../../services/unitService';
import useAbortController from '../../hooks/useAbortController';

/**
 * Fetches the Fortress-level ADA Accessibility Type options and maps them to the
 * { value, text } shape expected by the design-system MultiSelect. Backend already
 * sorts the options; the mapping preserves that order.
 */
const useAccessibilityTypesQuery = ({
  organizationId,
  propertyId,
  options,
}: {
  organizationId: string,
  propertyId: string,
  options?: QueryOptions,
}) => {
  const unitService = useMemo(() => new UnitService(), []);
  const abortControllerOptions = useAbortController();
  const queryKey = ['getAccessibilityTypes', organizationId, propertyId];

  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      unitService.getAccessibilityTypes(
        propertyId,
        organizationId,
        abortControllerOptions,
      ),
    options,
  );

  const memoizedOptions = useMemo(
    () =>
      (data ?? []).map((type) => ({
        value: type.id,
        text: type.name,
      })),
    [data],
  );

  return {
    options: memoizedOptions,
    isLoading,
  };
};

export default useAccessibilityTypesQuery;
